import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
	Button, TextField, Typography, Grid, Select, Card,
	CardHeader, CardContent, Divider, Accordion, AccordionSummary,
	AccordionDetails
} from '@material-ui/core';
import _ from 'underscore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useRouter from 'utils/useRouter';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker.js';
import { SetFormState } from '../../../../actions/OptOverallActions';

const schema = {
	stream: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	status: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	}
}));

const OptOverallDialogForm = (props) => {
	const { className, tableId, ...rest } = props;
	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.optOverallReducer.formState);
	const dropdowns = useSelector((state) => state.optOverallReducer.dropdowns);
	const role = useSelector(state => state.session.user.role);

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]:
					value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	useEffect(() => {
		const newTouched = {};

		if (formState.values.submit) {
			_.each(_.keys(schema), (item => {
				newTouched[item] = true;
			}));
		}

		const errors = validate(formState.values, schema, { fullMessages: false });

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
			touched: {
				...formState.touched,
				...newTouched
			},
		});
	}, [formState.values]);

	return (
		<div className={classes.fields}>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<TextField
						id="stream"
						name="stream"
						label="Stream"
						variant="outlined"
						error={_hasError('stream')}
						fullWidth
						helperText={
							_hasError('stream') ? formState.errors.stream[0] : null
						}
						value={formState.values.stream || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="category"
						name="category"
						label="Category"
						variant="outlined"
						error={_hasError('category')}
						fullWidth
						helperText={
							_hasError('category') ? formState.errors.category[0] : null
						}
						value={formState.values.category || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="type"
						name="type"
						label="Type"
						variant="outlined"
						error={_hasError('type')}
						fullWidth
						helperText={
							_hasError('type') ? formState.errors.type[0] : null
						}
						value={formState.values.type || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="tech"
						name="tech"
						hintText="Tech"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.tech) || []}
						value={formState.values.tech || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<SelectField
						id="cfnOwner"
						name="cfnOwner"
						hintText="CFN Owner"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.cfnOwner) || []}
						value={formState.values.cfnOwner || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="description"
						name="description"
						label="Description"
						variant="outlined"
						error={_hasError('description')}
						fullWidth
						helperText={
							_hasError('description') ? formState.errors.description[0] : null
						}
						value={formState.values.description || ''}
						onChange={_handleChange}
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					{/* <DateTimePicker
						id="reportedDate"
						name="reportedDate"
						label="Reported Date"
						style={{ width: '100%' }}
						value={formState.values.reportedDate || ''}
						onChange={(value) => _handleDateChange(value, 'reportedDate')}
						minutesStep={1}
					/> */}
					<DatePicker
						id="reportedDate"
						name="reportedDate"
						label="Reported Date"
						style={{ width: '100%' }}
						value={formState.values.reportedDate || ''}
						onChange={(value) => _handleDateChange(value, 'reportedDate')}
						format="DD-MM-YYYY"
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="vfUkComments"
						name="vfUkComments"
						label="VF UK Comments"
						variant="outlined"
						error={_hasError('vfUkComments')}
						fullWidth
						helperText={
							_hasError('vfUkComments') ? formState.errors.vfUkComments[0] : null
						}
						value={formState.values.vfUkComments || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="vfUkOwner"
						name="vfUkOwner"
						label="VF UK Owner"
						variant="outlined"
						error={_hasError('vfUkOwner')}
						fullWidth
						helperText={
							_hasError('vfUkOwner') ? formState.errors.vfUkOwner[0] : null
						}
						value={formState.values.vfUkOwner || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="area"
						name="area"
						label="Area"
						variant="outlined"
						error={_hasError('area')}
						fullWidth
						helperText={
							_hasError('area') ? formState.errors.area[0] : null
						}
						value={formState.values.area || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					{/* <DateTimePicker
						id="implementationDate"
						name="implementationDate"
						label="Implementation Date"
						style={{ width: '100%' }}
						value={formState.values.implementationDate || ''}
						onChange={(value) => _handleDateChange(value, 'implementationDate')}
						minutesStep={1}
					/> */}
					<DatePicker
						id="implementationDate"
						name="implementationDate"
						label="Implementation Date"
						style={{ width: '100%' }}
						value={formState.values.implementationDate || ''}
						onChange={(value) => _handleDateChange(value, 'implementationDate')}
						format="DD-MM-YYYY"
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					{/* <DateTimePicker
						id="closeDate"
						name="closeDate"
						label="Close Date"
						style={{ width: '100%' }}
						value={formState.values.closeDate || ''}
						onChange={(value) => _handleDateChange(value, 'closeDate')}
						minutesStep={1}
					/> */}
					<DatePicker
						id="closeDate"
						name="closeDate"
						label="Close Date"
						style={{ width: '100%' }}
						value={formState.values.closeDate || ''}
						onChange={(value) => _handleDateChange(value, 'closeDate')}
						format="DD-MM-YYYY"
					/>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={2}>
				<Grid item md={2} xs={2}>
					<SelectField
						id="status"
						name="status"
						hintText="Status"
						defaultValue=""
						menuItems={(dropdowns && dropdowns.status) || []}
						value={formState.values.status || ''}
						onChange={_handleChange}
						error={
							_hasError('status') ? formState.errors.status[0] : null
						}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="overallComments"
						name="overallComments"
						label="Overall Comments"
						variant="outlined"
						error={_hasError('overallComments')}
						fullWidth
						helperText={
							_hasError('overallComments') ? formState.errors.overallComments[0] : null
						}
						value={formState.values.overallComments || ''}
						onChange={_handleChange}
					/>
				</Grid>
				<Grid item md={2} xs={2}>
					<TextField
						id="overallImprovement"
						name="overallImprovement"
						label="Overall Improvement"
						variant="outlined"
						error={_hasError('overallImprovement')}
						fullWidth
						helperText={
							_hasError('overallImprovement') ? formState.errors.overallImprovement[0] : null
						}
						value={formState.values.overallImprovement || ''}
						onChange={_handleChange}
					/>
				</Grid>
			</Grid>
		</div>
	);
};

OptOverallDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default OptOverallDialogForm;
